import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useLazyGetFhirIgArtifactsQuery,
  useLazyGetFhirIgVersionsQuery,
} from '../rtk/api/datagroupsApi';
import { resolveIgUri, isSafeIgUri } from '../utils/fhirIg/fhirIg';
import { formatApiError } from '../utils/release/apiError';

/**
 * Orchestrates the FHIR IG (iframe domain, e.g. Clinical) selection flow:
 *
 *  1. `selectIg({ guide, subGroupChain, version })` fetches that guide's
 *     artifacts and resolves the `igUri` shown in the iframe, and loads the
 *     guide's publish-version list for the version selector.
 *  2. `changeVersion(v)` re-fetches artifacts for the same guide at a chosen
 *     publish version and reloads the iframe.
 *
 * Server calls live in RTK Query; this hook owns only the transient selection
 * state, mirroring `useSchemaData` for the schema domain.
 */
export function useFhirIg({ auth, groupName, urlPathSegment }) {
  const [igUri, setIgUri] = useState('');
  const [igMeta, setIgMeta] = useState(null);
  const [igLoading, setIgLoading] = useState(false);
  const [igError, setIgError] = useState(null);
  const [selectedIg, setSelectedIg] = useState(null); // { guide, subGroupChain }
  const [selectedVersion, setSelectedVersion] = useState('');
  const [versions, setVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);

  const [triggerGetArtifacts] = useLazyGetFhirIgArtifactsQuery();
  const [triggerGetVersions] = useLazyGetFhirIgVersionsQuery();

  const authed = !!auth?.user?.id_token;

  const reset = useCallback(() => {
    setIgUri('');
    setIgMeta(null);
    setIgLoading(false);
    setIgError(null);
    setSelectedIg(null);
    setSelectedVersion('');
    setVersions([]);
    setVersionsLoading(false);
  }, []);

  const fetchArtifacts = useCallback(
    async ({ guide, subGroupChain, version }) => {
      if (!authed || !groupName || !guide || !Array.isArray(subGroupChain) || !subGroupChain.length)
        return;

      setIgLoading(true);
      setIgError(null);

      try {
        const { data, error } = await triggerGetArtifacts(
          { groupName, subGroupChain, guide, version, urlPathSegment },
          false
        );
        if (error) throw error;

        const uri = resolveIgUri(data);
        if (!uri) throw new Error('This implementation guide has no published page to display.');
        if (!isSafeIgUri(uri)) throw new Error('This implementation guide URL is not supported.');

        setIgUri(uri);
        setIgMeta(data || null);
        setSelectedVersion(data?.publishVersion || version || '');
      } catch (err) {
        console.error('[FhirIg] artifacts fetch failed', err);
        setIgError(formatApiError(err, 'Failed to load the implementation guide.'));
        setIgUri('');
        setIgMeta(null);
      } finally {
        setIgLoading(false);
      }
    },
    [authed, groupName, urlPathSegment, triggerGetArtifacts]
  );

  const fetchVersions = useCallback(
    async ({ guide, subGroupChain }) => {
      if (
        !authed ||
        !groupName ||
        !guide ||
        !Array.isArray(subGroupChain) ||
        !subGroupChain.length
      ) {
        setVersions([]);
        return;
      }
      setVersionsLoading(true);
      try {
        const { data, error } = await triggerGetVersions(
          { groupName, subGroupChain, guide, urlPathSegment },
          false
        );
        if (error) throw error;
        setVersions(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('[FhirIg] versions fetch failed', err);
        setVersions([]);
      } finally {
        setVersionsLoading(false);
      }
    },
    [authed, groupName, urlPathSegment, triggerGetVersions]
  );

  /**
   * Select a guide: load its artifacts (iframe URL) and its version list.
   * @param {{ guide: string, subGroupChain: string[], version?: string }} params
   */
  const selectIg = useCallback(
    ({ guide, subGroupChain, version }) => {
      setSelectedIg({ guide, subGroupChain });
      fetchArtifacts({ guide, subGroupChain, version });
      fetchVersions({ guide, subGroupChain });
    },
    [fetchArtifacts, fetchVersions]
  );

  /** Re-fetch the current guide's iframe at a chosen publish version. */
  const changeVersion = useCallback(
    (event) => {
      const value = Array.isArray(event?.detail) ? event.detail[0] : (event?.detail ?? event);
      if (!value || !selectedIg?.guide) return;
      setSelectedVersion(value);
      fetchArtifacts({ ...selectedIg, version: value });
    },
    [selectedIg, fetchArtifacts]
  );

  // Clear selection when the group or authentication changes.
  useEffect(() => {
    reset();
  }, [groupName, authed, reset]);

  const hasSelection = useMemo(() => !!selectedIg?.guide, [selectedIg]);

  return {
    igUri,
    igMeta,
    igLoading,
    igError,
    versions,
    versionsLoading,
    selectedVersion,
    selectedIg,
    hasSelection,
    selectIg,
    changeVersion,
    reset,
  };
}
